<template>
  <div class="media-container">
    <div class="ah-head">
      <h1 class="vtm-page-head__title">{{ $t("menu.Adverts") }}</h1>
    </div>

    <!-- "如何投放广告" 顺序指引：可折叠，关闭状态持久化到 localStorage -->
    <div v-if="!guideDismissed" class="ah-guide">
      <div class="ah-guide__head">
        <span class="ah-guide__title"><i class="el-icon-info"></i> {{ $t("advertsGuide.title") }}</span>
        <button type="button" class="ah-guide__dismiss" @click="dismissGuide">{{ $t("advertsGuide.dismiss") }}</button>
      </div>
      <ol class="ah-guide__steps">
        <li>{{ $t("advertsGuide.step1") }}</li>
        <li>{{ $t("advertsGuide.step2") }}</li>
        <li>{{ $t("advertsGuide.step3") }}</li>
        <li>{{ $t("advertsGuide.step4") }}</li>
      </ol>
    </div>
    <button v-else type="button" class="ah-guide-reopen" @click="reopenGuide">
      <i class="el-icon-info"></i> {{ $t("advertsGuide.reopen") }}
    </button>

    <div class="ah-seg">
      <el-radio-group v-model="hubView" size="small">
        <el-radio-button label="ads">{{ $t("advertsHub.myAds") }}</el-radio-button>
        <el-radio-button v-if="hubGrants.adLibrary" label="adLibrary">{{ $t("advertsHub.adLibrary") }}</el-radio-button>
        <el-radio-button v-if="hubGrants.groups" label="groups">{{ $t("advertsHub.adGroups") }}</el-radio-button>
        <el-radio-button v-if="hubGrants.machineAds" label="machineAds">{{ $t("advertsHub.machineAds") }}</el-radio-button>
        <el-radio-button v-if="hubGrants.network" label="network">{{ $t("advertsHub.networkAds") }}</el-radio-button>
        <el-radio-button v-if="hubGrants.labels" label="labels">{{ $t("advertsHub.labels") }}</el-radio-button>
        <el-radio-button v-if="hubGrants.analytics" label="analytics">{{ $t("advertsHub.analytics") }}</el-radio-button>
      </el-radio-group>
    </div>
    <div v-show="hubView === 'ads'">
    <!-- Top Header with Upload, Filters, Search -->
    <div class="top-header">
      <div class="left-section">
        <button class="btn btn-upload" @click="showUploadModal = true"><i class="el-icon-upload"></i> Upload Media</button>
        <button 
          class="btn btn-assign"
          @click="openAssignModal"
          v-if="hasSelection"
        >
          <i class="el-icon-position"></i> Assign ({{ selectedCount }})
        </button>
        <button 
          class="btn btn-delete"
          @click="deleteSelected"
          v-if="hasSelection"
        >
          <i class="el-icon-delete"></i> Delete
        </button>
      </div>
      
      <div class="right-section">
        <div class="filter-buttons">
          <button 
            class="filter-btn" 
            :class="{ active: activeFilter === 'All' }"
            @click="activeFilter = 'All'"
          >
            All
          </button>
          <button 
            class="filter-btn" 
            :class="{ active: activeFilter === 'Screen' }"
            @click="activeFilter = 'Screen'"
          >
            <i class="el-icon-monitor"></i> Screen
          </button>
          <button 
            class="filter-btn" 
            :class="{ active: activeFilter === 'Banner' }"
            @click="activeFilter = 'Banner'"
          >
            <i class="el-icon-picture"></i> Banner
          </button>
        </div>
        
        <div class="search-reset">
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="Search ads..."
            class="search-box"
          />
          <button class="reset-btn" @click="resetFilters">↻ Reset</button>
        </div>
      </div>
    </div>

    <!-- Selection Toolbar -->
    <div class="selection-toolbar">
      <div class="left-toolbar">
        <label class="select-all-label">
          <input 
            type="checkbox" 
            v-model="selectAllCheckbox"
            @change="toggleSelectAll"
            class="select-checkbox"
          />
          <span>Select All ({{ filteredMedia.length }})</span>
        </label>
        
        <!-- <div class="tag-as-section" v-if="hasSelection">
          <span class="tag-as-label">Tag as:</span>
          <button 
            class="tag-btn" 
            :class="{ active: tagToAssign === 'Screen' }"
            @click="applyTag('Screen')"
          >
            <i class="el-icon-monitor"></i> Screen
          </button>
          <button 
            class="tag-btn" 
            :class="{ active: tagToAssign === 'Banner' }"
            @click="applyTag('Banner')"
          >
            <i class="el-icon-picture"></i> Banner
          </button>
        </div> -->
      </div>
    </div>

    <!-- Media Grid -->
    <div class="media-grid-wrapper">
      <div class="media-grid">
        <div 
          v-for="item in filteredMedia" 
          :key="item.id" 
          class="media-card"
          :class="{ selected: item.selected }"
          @click="item.selected = !item.selected; updateSelectAllCheckbox()"
        >
          <div class="card-header">
            <input 
              type="checkbox" 
              v-model="item.selected"
              @change="updateSelectAllCheckbox"
              class="card-checkbox"
            />
            <div class="card-actions">
              <button
                type="button"
                class="card-edit-btn"
                title="Download"
                @click.stop="downloadAd(item)"
              >
                <i class="el-icon-download"></i>
              </button>
              <button
                type="button"
                class="card-edit-btn"
                :title="$t('common.edit') || 'Edit'"
                @click.stop="openEditModal(item)"
              >
                <i class="el-icon-edit"></i>
              </button>
            </div>
          </div>
          
          <div class="card-image">
            <!-- Display image for image media types -->
            <img v-if="item.mediaType !== 'video'" :src="item.image" :alt="item.title" @error="handleImageError" />
            
            <!-- Display video for video media types -->
            <video v-else :src="item.image" :alt="item.title" class="card-video" autoplay muted></video>
          </div>
          
          <div class="card-content">
            <h3 class="card-title">{{ item.title }}</h3>
            <p class="card-desc">{{ item.desc }}</p>
            <div class="card-footer">
              <span class="card-date">{{ item.date }}</span>
              <span class="card-tag" :class="`tag-${item.tag.toLowerCase()}`">
                <i :class="item.tag === 'Screen' ? 'el-icon-monitor' : 'el-icon-picture'"></i>
                {{ item.tag }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredMedia.length === 0" class="empty-state">
        <p>No media found</p>
      </div>
    </div>

    <!-- Upload Media Modal -->
    <div v-if="showUploadModal" class="modal-overlay" @click.self="closeUploadModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ editingAdId ? 'Edit Ad' : 'Upload New Ad' }}</h2>
          <button class="modal-close" @click="closeUploadModal">✕</button>
        </div>

        <div class="modal-body">
          <!-- Hidden file input — always in DOM so ref is always available -->
          <input
            type="file"
            ref="fileInput"
            @change="handleFileSelect"
            accept=".jpg,.jpeg,.png,.gif,.mp4,.webm"
            class="hidden-file-input"
          />

          <!-- Drag & Drop Area — hidden when file selected or existing media (edit mode) -->
          <div
            v-if="!uploadFormData.file && !uploadFormData.uploadedFileUrl"
            class="file-upload-area"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="handleFileDrop"
            :class="{ 'dragging': dragOver }"
          >
            <div class="upload-icon"><i class="el-icon-upload"></i></div>
            <p class="upload-text">Drag & drop media files here or click to browse</p>
            <p class="upload-support">Supports JPG, PNG, MP4, WebM</p>
            <button type="button" class="browse-btn" @click="triggerFileInput">Browse Files</button>
          </div>

          <!-- File Preview — shown when file selected or existing media (edit mode) -->
          <div v-if="uploadFormData.file || uploadFormData.uploadedFileUrl" class="uploaded-file-preview">
            <div class="preview-image-container">
              <!-- Loading overlay while S3 upload is in progress -->
              <div v-if="loading" class="preview-loading-overlay">
                <i class="el-icon-loading"></i>
                <span>Uploading...</span>
              </div>
              <img
                v-if="uploadFormData.mediaType === 'image'"
                :src="localPreviewUrl || uploadFormData.uploadedFileUrl"
                alt="Preview"
                class="preview-image"
              />
              <video
                v-else
                :src="localPreviewUrl || uploadFormData.uploadedFileUrl"
                style="width:100%;height:150px;border-radius:4px;object-fit:cover;"
              ></video>
            </div>
            <div class="preview-info">
              <p class="preview-filename">
                <i
                  :class="loading ? 'el-icon-loading' : 'el-icon-circle-check'"
                  :style="{ color: loading ? 'var(--vtm-mint-ink)' : 'var(--vtm-ok)' }"
                ></i>
                <strong>{{ uploadFormData.file ? uploadFormData.file.name : 'Current media' }}</strong>
              </p>
              <p v-if="loading" class="upload-progress-text">Uploading to server...</p>
              <p v-else-if="uploadFormData.uploadedFileUrl" class="upload-success-text">{{ editingAdId ? 'Existing file' : 'Upload complete ✓' }}</p>
              <button type="button" class="clear-upload-btn" @click="clearUploadedFile" :disabled="loading">
                <i class="el-icon-delete"></i> Clear
              </button>
            </div>
          </div>

          <!-- Form Fields -->
          <div class="form-group">
            <label class="form-label">Title</label>
            <input
              v-model="uploadFormData.title"
              type="text"
              placeholder="Ad title"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Company</label>
            <input
              v-model="uploadFormData.company"
              type="text"
              placeholder="Company name"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Placement Type</label>
            <div class="placement-options">
              <button
                type="button"
                class="placement-btn"
                :class="{ active: uploadFormData.placementType === 'screen' }"
                @click="uploadFormData.placementType = 'screen'"
              >
                <i class="el-icon-monitor"></i> Screen
              </button>
              <button
                type="button"
                class="placement-btn"
                :class="{ active: uploadFormData.placementType === 'banner' }"
                @click="uploadFormData.placementType = 'banner'"
              >
                <i class="el-icon-picture"></i> Banner
              </button>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-cancel" @click="closeUploadModal">Cancel</button>
          <button class="btn btn-upload-confirm" :disabled="loading" @click="editingAdId ? updateAd() : uploadAd()">
            {{ loading ? (editingAdId ? 'Updating...' : 'Uploading...') : (editingAdId ? 'Update' : 'Upload') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Assign Ads to Machines Modal -->
    <div v-if="showAssignModal" class="modal-overlay" @click.self="closeAssignModal">
      <div class="modal-content assign-modal">
        <div class="modal-header">
          <h2>Assign Ads to Machines</h2>
          <button class="modal-close" @click="closeAssignModal">✕</button>
        </div>

        <div class="modal-body" @click="dropdownOpen = false; groupDropdownOpen = false">
          <!-- Selected Ads Info -->
          <div class="assign-info">
            <p class="assign-count">Assigning {{ selectedCount }} ad<span v-if="selectedCount>1">s</span></p>
            
            <div class="selected-ads-list">
              <div v-for="(ad, index) in getSelectedAds(4)" :key="ad.id" class="selected-ad-item">
                <img
                  v-if="ad.mediaType !== 'video'"
                  :src="ad.image"
                  :alt="ad.title"
                  class="ad-thumbnail"
                  @error="handleImageError"
                />
                <video
                  v-else
                  :src="ad.image"
                  class="ad-thumbnail ad-thumbnail-video"
                  muted
                  playsinline
                  autoplay
                  loop
                />
                <span class="ad-title">{{ ad.title }}</span>
              </div>
              <div v-if="selectedCount > 4" class="selected-ad-item more-badge">
                <div class="more-count">+{{ selectedCount - 4 }}</div>
                <span class="ad-title">more</span>
              </div>
            </div>
          </div>

          <!-- Start Date & End Date (single range picker) -->
          <div class="assign-dates-row">
            <div class="form-group" style="flex: 1;">
              <label class="form-label">Start Date & Time – End Date & Time</label>
              <el-date-picker
                v-model="assignForm.dateRange"
                type="datetimerange"
                range-separator="-"
                :start-placeholder="$t('searchOrder.start')"
                :end-placeholder="$t('searchOrder.end')"
                value-format="yyyy-MM-dd HH:mm:ss"
                format="yyyy-MM-dd HH:mm:ss"
                :default-time="['00:00:00', '23:59:59']"
                class="assign-datetime-picker assign-datetime-range-picker"
                style="width: 100%; max-width: 360px;"
                clearable
              />
            </div>
          </div>

          <!-- Assign To Dropdown -->
          <div class="form-group">
            <label class="form-label">Assign To</label>
            <div class="custom-dropdown" @click.stop>
              <button @click.stop="dropdownOpen = !dropdownOpen; groupDropdownOpen=false" class="dropdown-trigger">
                <i :class="assignForm.assignTo === 'Machine Group' ? 'el-icon-office-building' : 'el-icon-setting'"></i>
                {{ assignForm.assignTo }}
                <i class="el-icon-arrow-down dropdown-arrow"></i>
              </button>
              <div v-if="dropdownOpen" class="dropdown-menu">
                <div class="dropdown-item" @click="assignForm.assignTo = 'Machine Group'; dropdownOpen = false">
                  <i v-if="assignForm.assignTo === 'Machine Group'" class="el-icon-check checkmark-left"></i>
                  <i v-else style="width: 16px;"></i>
                  <i class="el-icon-office-building"></i> Machine Group
                </div>
                <div class="dropdown-item" @click="assignForm.assignTo = 'Individual Machine'; dropdownOpen = false">
                  <i v-if="assignForm.assignTo === 'Individual Machine'" class="el-icon-check checkmark-left"></i>
                  <i v-else style="width: 16px;"></i>
                  <i class="el-icon-setting"></i> Individual Machines
                </div>
              </div>
            </div>
          </div>

          <!-- Select Group Dropdown (Machine Group) -->
          <div v-if="assignForm.assignTo === 'Machine Group'" class="form-group">
            <label class="form-label">Select Group</label>
            <div class="custom-dropdown" @click.stop>
              <button @click.stop="groupDropdownOpen = !groupDropdownOpen" class="dropdown-trigger">
                <i class="el-icon-folder"></i>
                {{ assignGroupsLoading ? 'Loading...' : selectedGroupName }}
                <i class="el-icon-arrow-down dropdown-arrow"></i>
              </button>
              <div v-if="groupDropdownOpen" class="dropdown-menu">
                <div v-if="assignGroupsLoading" class="dropdown-item assign-loading-item">Loading groups...</div>
                <template v-else>
                  <div v-for="group in machineGroups" :key="group.id" class="dropdown-item" @click="assignForm.selectedGroupId = group.id; groupDropdownOpen = false">
                    <i v-if="assignForm.selectedGroupId === group.id" class="el-icon-check checkmark-left"></i>
                    <i v-else style="width: 16px;"></i>
                    <i class="el-icon-folder"></i> {{ group.name }}
                  </div>
                  <div v-if="machineGroups.length === 0" class="dropdown-item assign-empty-item">No groups found</div>
                </template>
              </div>
            </div>
          </div>

          <!-- Individual Machines Section -->
          <div v-if="assignForm.assignTo === 'Individual Machine'" class="machines-section">
            <div class="form-group">
              <label class="form-label">Select Machines ({{ selectedMachinesCount }} selected)</label>
              <input 
                v-model="machineSearchQuery"
                type="text"
                placeholder="Search machines..."
                class="form-input search-machines"
              />
            </div>

            <div class="machines-list">
              <div v-if="assignMachinesLoading" class="assign-loading">Loading machines...</div>
              <template v-else>
                <div v-for="machine in filteredMachines" :key="machine.id" class="machine-row">
                  <input 
                    type="checkbox"
                    v-model="machine.selected"
                    class="machine-checkbox"
                  />
                  <div class="machine-info">
                    <div class="machine-id">{{ machine.equipmentName ? machine.equipmentName + ' (' + (machine.equipmentNo || machine.id || '') + ')' : (machine.equipmentNo || machine.id || 'N/A') }}</div>
                    <div class="machine-location">{{ machine.address }}</div>
                    <div v-if="machine.group" class="machine-group">{{ machine.group }}</div>
                  </div>
                  <button class="machine-settings-btn">
                    <i class="el-icon-setting"></i>
                  </button>
                </div>
                <div v-if="!assignMachinesLoading && filteredMachines.length === 0" class="assign-empty">No machines found</div>
              </template>
            </div>
          </div>
        </div>

        <div class="modal-footer" @click="dropdownOpen = false; groupDropdownOpen = false">
          <button class="btn btn-cancel" @click="closeAssignModal">Cancel</button>
          <button class="btn btn-assign-confirm" @click="handleAssignAds">
            {{ assignForm.assignTo === 'Machine Group' ? 'Assign to Group' : 'Assign to ' + selectedMachinesCount + ' Machine' + (selectedMachinesCount !== 1 ? 's' : '') }}
          </button>
        </div>
      </div>
    </div>
    </div>
    <div v-show="hubView === 'adLibrary'" class="ah-embed"><ad-library v-if="adLibraryMounted"></ad-library></div>
    <div v-show="hubView === 'groups'" class="ah-embed"><advert-group v-if="groupsMounted"></advert-group></div>
    <div v-show="hubView === 'machineAds'" class="ah-embed"><machine-advert-group v-if="machineAdsMounted" :initial-equipment-no="routeEquipmentNo"></machine-advert-group></div>
    <div v-show="hubView === 'network'" class="ah-embed">
      <network-ads v-if="networkMounted && isHubAdmin"></network-ads>
      <view-network-ads v-if="networkMounted && !isHubAdmin"></view-network-ads>
    </div>
    <div v-show="hubView === 'labels'" class="ah-embed"><machine-advert-label v-if="labelsMounted"></machine-advert-label></div>
    <div v-if="hubView === 'analytics'" class="ah-embed"><adver-statistics></adver-statistics></div>
  </div>
</template>

<script>
import request from '@/interface/index'
import url from '@/interface/url'
import methods from '@/interface/method'
import Cookies from 'js-cookie'
import { grantedLinks } from "@/utils/navSections";
import adLibrary from "@/views/adverts/AdLibrary";
import advertGroup from "@/views/adverts/AdvertGroup";
import machineAdvertGroup from "@/views/adverts/MachineAdvertGroup";
import networkAds from "@/views/adverts/NetworkAds";
import viewNetworkAds from "@/views/adverts/ViewNetworkAds";
import machineAdvertLabel from "@/views/adverts/MachineAdvertLabel";
import adverStatistics from "@/views/statistics/AdverStatistics";

export default {
  name: 'Adverts',
  components: {
    adLibrary,
    advertGroup,
    machineAdvertGroup,
    networkAds,
    viewNetworkAds,
    machineAdvertLabel,
    adverStatistics
  },
  data() {
    return {
      hubView: 'ads',
      adLibraryMounted: false,
      groupsMounted: false,
      machineAdsMounted: false,
      networkMounted: false,
      labelsMounted: false,
      activeFilter: 'All',
      searchQuery: '',
      tagToAssign: 'Screen',
      selectAllCheckbox: false,
      showUploadModal: false,
      editingAdId: null,
      showAssignModal: false,
      loading: false,
      dragOver: false,
      localPreviewUrl: null,
      uploadFormData: {
        title: '',
        company: '',
        mediaType: 'video',
        placementType: 'screen',
        file: null,
        uploadedFileUrl: null,
        videoDuration: 30
      },
      assignForm: {
        assignTo: 'Machine Group',
        selectedGroupId: null,
        dateRange: null
      },
      machineSearchQuery: '',
      dropdownOpen: false,
      groupDropdownOpen: false,
      machineGroups: [],
      individualMachines: [],
      assignGroupsLoading: false,
      assignMachinesLoading: false,
      media: [], // Will be populated by API call
      routeEquipmentNo: '',
      guideDismissed: false
    }
  },
  mounted() {
    this.fetchAdsList();
    this.applyRouteView();
    this.guideDismissed = this.readGuideDismissed();
  },
  watch: {
    hubView(v) {
      if (v === 'adLibrary') this.adLibraryMounted = true;
      if (v === 'groups') this.groupsMounted = true;
      if (v === 'machineAds') this.machineAdsMounted = true;
      if (v === 'network') this.networkMounted = true;
      if (v === 'labels') this.labelsMounted = true;
    }
  },
  computed: {
    isHubAdmin() {
      return Cookies.get('roleId') === '0';
    },
    // 各分段对应的旧页面授权（子账号可能仅被授予部分页面）
    hubGrants() {
      const g = grantedLinks(this.$store.state.menuList);
      return {
        adLibrary: g.has('/adverts/ad-library'),
        groups: g.has('/adverts/advert-group'),
        machineAds: g.has('/adverts/machine-advert-group'),
        network: g.has('/adverts/network-ads') || g.has('/adverts/view-network-ads'),
        labels: g.has('/adverts/machine-advert-label'),
        analytics: g.has('/statistics/adver-statistics')
      };
    },
    filteredMedia() {
      return this.media.filter(item => {
        const matchesFilter = this.activeFilter === 'All' || item.tag === this.activeFilter;
        const matchesSearch = !this.searchQuery || 
          item.title.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          item.desc.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          item.tag.toLowerCase().includes(this.searchQuery.toLowerCase());
        return matchesFilter && matchesSearch;
      });
    },
    selectedCount() {
      return this.media.filter(item => item.selected).length;
    },
    hasSelection() {
      return this.selectedCount > 0;
    },
    filteredMachines() {
      if (!this.machineSearchQuery) {
        return this.individualMachines;
      }
      const q = this.machineSearchQuery.toLowerCase();
      return this.individualMachines.filter(machine =>
        (machine.equipmentNo || machine.id || '').toString().toLowerCase().includes(q) ||
        (machine.equipmentName || '').toLowerCase().includes(q) ||
        (machine.address || '').toLowerCase().includes(q) ||
        (machine.group || '').toLowerCase().includes(q)
      );
    },
    selectedMachinesCount() {
      return this.individualMachines.filter(machine => machine.selected).length;
    },
    selectedGroupName() {
      if (this.assignForm.selectedGroupId == null) return 'Select group';
      const g = this.machineGroups.find(gr => gr.id === this.assignForm.selectedGroupId);
      return g ? g.name : 'Select group';
    },
  },
  methods: {
    // 深链支持：读取 ?view=&equipmentNo= 定位到指定分段并预筛选到某台设备（来自设备详情飞出的"广告"页签）
    applyRouteView() {
      const validViews = ['ads', 'groups', 'machineAds', 'network', 'labels', 'analytics'];
      const view = this.$route && this.$route.query ? this.$route.query.view : null;
      if (typeof view === 'string' && validViews.indexOf(view) !== -1) {
        const isGranted = view === 'ads' || !!this.hubGrants[view];
        if (isGranted) {
          this.hubView = view;
        }
      }
      const eq = this.$route && this.$route.query ? this.$route.query.equipmentNo : null;
      if (typeof eq === 'string' && eq) {
        this.routeEquipmentNo = eq;
      }
    },
    readGuideDismissed() {
      try {
        return localStorage.getItem('vtm-adverts-guide-dismissed') === '1';
      } catch (e) {
        return false;
      }
    },
    dismissGuide() {
      this.guideDismissed = true;
      try {
        localStorage.setItem('vtm-adverts-guide-dismissed', '1');
      } catch (e) {
        // ignore storage errors (e.g. private mode)
      }
    },
    reopenGuide() {
      this.guideDismissed = false;
      try {
        localStorage.removeItem('vtm-adverts-guide-dismissed');
      } catch (e) {
        // ignore storage errors
      }
    },
    fetchAdsList() {
      this.$api
        .request(this.$url.Adverts.listAdvertByGroupId, this.$method.get)
        .then(res => {
          if (res.code === 200 || res.code === 0) {
            // Transform API response to match media structure
            this.media = res.data.map(ad => {
              // Determine media type from URL
              const mediaUrl = ad.thum || ad.url;
              const fileExtension = mediaUrl ? mediaUrl.split('.').pop().toLowerCase() : '';
              let mediaType = 'image';
              
              if (['mp4', 'webm', 'mov', 'avi'].includes(fileExtension)) {
                mediaType = 'video';
              } else if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(fileExtension)) {
                mediaType = 'image';
              }
              
              return {
                id: ad.adId ?? ad.id,
                title: ad.head,
                desc: ad.advertiser,
                date: ad.createTime,
                tag: ad.display === 2 ? 'Banner' : 'Screen', // display 1 = Screen, 2 = Banner
                image: mediaUrl,
                mediaType: mediaType, // 'image' or 'video'
                selected: false,
                // Keep original API data
                adId: ad.adId ?? ad.id,
                type: ad.type,
                url: ad.url,
                thum: ad.thum,
                price: ad.price,
                startTime: ad.startTime,
                endTime: ad.endTime,
                display: ad.display,
                len: ad.len,
                sort: ad.sort
              };
            });
          } else {
            console.error('Failed to fetch ads:', res.msg);
          }
        })
        .catch(err => {
          console.error('Error fetching ads:', err);
        });
    },
    handleImageError(event) {
      if (event && event.target) {
        event.target.src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="200" height="150" viewBox="0 0 200 150"><rect fill="#f0f0f0" width="200" height="150"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#999" font-size="12" font-family="sans-serif">No image</text></svg>');
      }
    },
    deleteSelected() {
      const selected = this.media.filter(item => item.selected);
      if (selected.length === 0) {
        this.$message.warning('Please select at least one ad to delete.');
        return;
      }
      const ids = [...new Set(selected.map(item => (item.adId != null ? item.adId : item.id)).filter(id => id != null && String(id).trim() !== ''))];
      if (ids.length === 0) {
        this.$message.warning('Selected ads have no valid ID.');
        return;
      }
      this.$confirm(`Delete ${ids.length} selected ad(s)?`, 'Confirm Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        const idList = ids.map(id => String(id));
        request.request(url.userEquipmentAd.deleteAdGallery, methods.delete, idList)
          .then(res => {
            const ok = res.code === 0 || res.code === 200;
            if (ok && res.data === true) {
              this.$message.success(res.msg || 'Deleted successfully.');
              this.media = this.media.filter(item => !item.selected);
              this.selectAllCheckbox = false;
              this.updateSelectAllCheckbox();
              this.fetchAdsList();
            } else {
              this.$message.error(res.msg || 'Delete failed.');
            }
          })
          .catch(err => {
            console.error('deleteAdGallery', err);
            this.$message.error(err.message || err.msg || 'Failed to delete ads.');
          });
      }).catch(() => {});
    },
    applyTag(tag) {
      this.media.forEach(item => {
        if (item.selected) {
          item.tag = tag;
          item.display = tag === 'Screen' ? 1 : 2; // 1 = Screen, 2 = Banner
        }
      });
      this.selectAllCheckbox = false;
      this.tagToAssign = 'Screen';
    },
    toggleSelectAll(event) {
      this.filteredMedia.forEach(item => {
        item.selected = event.target.checked;
      });
    },
    updateSelectAllCheckbox() {
      const allSelected = this.filteredMedia.every(item => item.selected);
      const someSelected = this.filteredMedia.some(item => item.selected);
      this.selectAllCheckbox = allSelected;
    },
    resetFilters() {
      this.activeFilter = 'All';
      this.searchQuery = '';
      this.media.forEach(item => {
        item.selected = false;
      });
      this.selectAllCheckbox = false;
      this.tagToAssign = 'Screen';
    },
    triggerFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    clearUploadedFile() {
      if (this.localPreviewUrl) {
        URL.revokeObjectURL(this.localPreviewUrl);
        this.localPreviewUrl = null;
      }
      this.uploadFormData.uploadedFileUrl = null;
      this.uploadFormData.file = null;
      this.uploadFormData.videoDuration = 30;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    closeUploadModal() {
      if (this.localPreviewUrl) {
        URL.revokeObjectURL(this.localPreviewUrl);
        this.localPreviewUrl = null;
      }
      this.showUploadModal = false;
      this.editingAdId = null;
      this.uploadFormData = {
        title: '',
        company: '',
        mediaType: 'video',
        placementType: 'screen',
        file: null,
        uploadedFileUrl: null,
        videoDuration: 30
      };
      this.dragOver = false;
      this.loading = false;
    },
    downloadAd(item) {
      const src = item.image;
      if (!src) return;
      const a = document.createElement('a');
      a.href = src;
      a.download = item.title || 'advertisement';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    openEditModal(item) {
      this.editingAdId = item.adId ?? item.id;
      this.uploadFormData = {
        title: item.title || '',
        company: item.desc || '',
        mediaType: item.mediaType || 'image',
        placementType: item.tag === 'Banner' ? 'banner' : 'screen',
        file: null,
        uploadedFileUrl: item.url || item.thum || item.image || null,
        videoDuration: item.len || 30
      };
      this.localPreviewUrl = null;
      this.loading = false;
      this.dragOver = false;
      this.showUploadModal = true;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    handleFileDrop(e) {
      this.dragOver = false;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.handleFileValidationAndUpload(files[0]);
      }
    },
    handleFileSelect(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.handleFileValidationAndUpload(files[0]);
      }
    },
    handleFileValidationAndUpload(file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4', 'video/webm'];
      const allowedExtensions = /\.(jpg|jpeg|png|gif|mp4|webm)$/i;

      if (!allowedTypes.includes(file.type) && !allowedExtensions.test(file.name)) {
        this.$message.error('Unsupported file type. Please upload JPG, PNG, MP4, or WebM files only');
        return;
      }

      const isImage = /\.(jpg|jpeg|png|gif)$/i.test(file.name);
      const isVideo = /\.(mp4|webm)$/i.test(file.name);

      if (isImage && file.size > 5 * 1024 * 1024) {
        this.$message.error(`Image size must not exceed 5MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        return;
      }
      if (isVideo && file.size > 100 * 1024 * 1024) {
        this.$message.error(`Video size must not exceed 100MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        return;
      }

      // Set file immediately so the drag-drop area hides and preview shows
      this.uploadFormData.file = file;
      this.uploadFormData.mediaType = isImage ? 'image' : 'video';

      // Create a local object URL for instant preview before S3 upload completes
      if (this.localPreviewUrl) {
        URL.revokeObjectURL(this.localPreviewUrl);
      }
      this.localPreviewUrl = URL.createObjectURL(file);

      this.uploadFileToS3(file);
    },
    uploadFileToS3(file) {
      const formData = new FormData();
      formData.append('file', file);

      this.loading = true;

      request.upload(url.Upload.advert, formData)
        .then(response => {
          this.loading = false;
          if (response.code === 0) {
            this.uploadFormData.uploadedFileUrl = response.data;
            this.$message.success('File uploaded successfully!');
            if (this.uploadFormData.mediaType === 'video') {
              this.getVideoDuration(response.data);
            }
          } else {
            this.$message.error(response.msg || 'File upload failed');
            this.clearUploadedFile();
          }
        })
        .catch(error => {
          this.loading = false;
          console.error('Error uploading file:', error);
          this.$message.error('Error uploading file to server');
          this.clearUploadedFile();
        });
    },
    getVideoDuration(videoUrl) {
      const video = document.createElement('video');
      video.src = videoUrl;
      video.addEventListener('loadedmetadata', () => {
        this.uploadFormData.videoDuration = Math.ceil(video.duration);
      });
      video.addEventListener('error', () => {
        this.uploadFormData.videoDuration = 30;
      });
    },
    uploadAd() {
      if (!this.uploadFormData.uploadedFileUrl) {
        this.$message.error('Please select and upload a file first');
        return;
      }

      const adData = {
        head: this.uploadFormData.title,
        advertiser: this.uploadFormData.company,
        url: this.uploadFormData.uploadedFileUrl,
        type: this.uploadFormData.mediaType === 'video' ? 1 : 0,
        display: this.uploadFormData.placementType === 'screen' ? 1 : 2, // 1 = Screen, 2 = Banner
        len: this.uploadFormData.videoDuration || (this.uploadFormData.mediaType === 'video' ? 30 : 15),
        thum: this.uploadFormData.uploadedFileUrl,
        adType: 0  // 0 = regular ad (gallery)
      };

      request.request(url.Adverts.insert, methods.post, adData)
        .then(response => {
          if (response.code === 0) {
            this.$message.success('Ad created successfully!');
            this.closeUploadModal();
            this.fetchAdsList();
          } else {
            this.$message.error(response.msg || 'Failed to create ad');
          }
        })
        .catch(error => {
          console.error('Error creating ad:', error);
          this.$message.error('Error creating ad');
        });
    },
    updateAd() {
      if (!this.uploadFormData.uploadedFileUrl) {
        this.$message.error('Please select and upload a file first');
        return;
      }

      const adData = {
        id: this.editingAdId,
        head: this.uploadFormData.title,
        advertiser: this.uploadFormData.company,
        url: this.uploadFormData.uploadedFileUrl,
        type: this.uploadFormData.mediaType === 'video' ? 1 : 0,
        display: this.uploadFormData.placementType === 'screen' ? 1 : 2, // 1 = Screen, 2 = Banner
        len: this.uploadFormData.videoDuration || (this.uploadFormData.mediaType === 'video' ? 30 : 15),
        thum: this.uploadFormData.uploadedFileUrl,
        adType: 0  // 0 = regular ad (gallery)
      };

      request.request(url.Adverts.update, methods.put, adData)
        .then(response => {
          if (response.code === 0) {
            this.$message.success('Ad updated successfully!');
            this.closeUploadModal();
            this.fetchAdsList();
          } else {
            this.$message.error(response.msg || 'Failed to update ad');
          }
        })
        .catch(error => {
          console.error('Error updating ad:', error);
          this.$message.error('Error updating ad');
        });
    },
    getSelectedAds(limit) {
      return this.media.filter(item => item.selected).slice(0, limit);
    },
    openAssignModal() {
      this.showAssignModal = true;
      this.machineSearchQuery = '';
      this.groupDropdownOpen = false;
      this.dropdownOpen = false;
      this.assignForm.selectedGroupId = null;
      this.assignForm.dateRange = null;
      this.fetchMachineGroups();
      this.fetchIndividualMachines();
    },
    closeAssignModal() {
      this.showAssignModal = false;
      this.machineSearchQuery = '';
      this.groupDropdownOpen = false;
      this.dropdownOpen = false;
      this.assignForm.selectedGroupId = null;
      this.assignForm.dateRange = null;
      this.individualMachines.forEach(m => { m.selected = false; });
      this.machineGroups.forEach(g => { g.selected = false; });
    },
    fetchMachineGroups() {
      this.assignGroupsLoading = true;
      const uid = Cookies.get('uid');
      request.request(url.UserMachineGroup.fetchGroupMachines, methods.get, uid ? { uId: uid } : {})
        .then(response => {
          if (response.code === 0) {
            const groups = response.data || [];
            this.machineGroups = groups.map(item => ({
              id: item.groupId,
              name: item.groupName,
              createdDate: item.createdDate ? String(item.createdDate).slice(0, 10) : '',
              machineCount: item.machineCount != null ? item.machineCount : 0,
              selected: false
            }));
          } else {
            this.$message.error(response.msg || 'Failed to load machine groups');
          }
        })
        .catch(error => {
          console.error('Error fetching machine groups:', error);
          this.$message.error('Error fetching machine groups');
        })
        .finally(() => {
          this.assignGroupsLoading = false;
        });
    },
    fetchIndividualMachines() {
      this.assignMachinesLoading = true;
      const uid = Cookies.get('uid');
      request.request(url.UserMachine.fetchMachineDetails, methods.get, uid ? { uId: uid } : {})
        .then(res => {
          if (res.code === 0 && res.data) {
            const list = Array.isArray(res.data) ? res.data : (res.data.records || []);
            this.individualMachines = list.map(item => this.mapSelectRecordToMachine(item));
          } else {
            this.$message.error(res.msg || 'Failed to load machines');
          }
        })
        .catch(error => {
          console.error('Error fetching individual machines:', error);
          this.$message.error('Error fetching individual machines');
        })
        .finally(() => {
          this.assignMachinesLoading = false;
        });
    },
    mapSelectRecordToMachine(record) {
      const id = record.equipmentId != null ? record.equipmentId : record.id;
      const equipmentNo = record.equipmentNum != null ? record.equipmentNum : record.equipmentNo;
      const equipmentName = (record.equipmentName != null ? record.equipmentName : record.name) || equipmentNo || '';
      const address = record.locale != null && record.locale !== '' ? record.locale : '';
      const group = record.teamName != null ? record.teamName : '';
      return {
        id,
        equipmentNo: equipmentNo != null ? equipmentNo : '',
        equipmentName: equipmentName != null ? equipmentName : '',
        address,
        group,
        selected: false
      };
    },
    handleAssignAds() {
      const selectedAds = this.media.filter(item => item.selected);
      if (selectedAds.length === 0) {
        this.$message.warning('Please select at least one ad to assign');
        return;
      }

      if (!this.assignForm.dateRange || this.assignForm.dateRange.length !== 2) {
        this.$message.warning('Please select Start Date and End Date');
        return;
      }
      const start = new Date(this.assignForm.dateRange[0]);
      const end = new Date(this.assignForm.dateRange[1]);
      if (end < start) {
        this.$message.warning('End date must be on or after start date');
        return;
      }

      const isGroup = this.assignForm.assignTo === 'Machine Group';
      if (isGroup && this.assignForm.selectedGroupId == null) {
        this.$message.warning('Please select a group');
        return;
      }
      if (!isGroup) {
        const selectedMachines = this.individualMachines.filter(m => m.selected);
        if (selectedMachines.length === 0) {
          this.$message.warning('Please select at least one machine');
          return;
        }
      }

      const adIdValue = (ad) => ad.id ?? ad.adId;
      const validSelected = selectedAds.filter(ad => adIdValue(ad) != null && adIdValue(ad) !== '');
      if (validSelected.length === 0) {
        this.$message.warning('Selected ads have no valid id. Please refresh and try again.');
        return;
      }
      if (validSelected.length < selectedAds.length) {
        this.$message.warning(`${selectedAds.length - validSelected.length} selected ad(s) skipped (no id).`);
      }

      const target = isGroup ? 1 : 2;
      const groupIds = isGroup ? [String(this.assignForm.selectedGroupId)] : [];
      const equipmentIds = isGroup ? [] : this.individualMachines.filter(m => m.selected).map(m => String(m.id));

      const payload = validSelected.map(ad => ({
        adId: String(adIdValue(ad)),
        startDate: this.assignForm.dateRange[0],
        endDate: this.assignForm.dateRange[1],
        target,
        groupIds,
        equipmentIds
      }));

      request.request(url.userEquipmentAd.galleryAdAssign, methods.post, payload)
        .then(res => {
          if (res.code === 0) {
            this.$message.success('Ads assigned successfully');
            this.media.forEach(item => { item.selected = false; });
            this.selectAllCheckbox = false;
            this.individualMachines.forEach(m => { m.selected = false; });
            this.machineSearchQuery = '';
            this.assignForm.selectedGroupId = null;
            this.assignForm.dateRange = null;
            this.closeAssignModal();
            this.fetchAdsList();
          } else {
            this.$message.error(res.msg || 'Failed to assign ads');
          }
        })
        .catch(err => {
          console.error('Error assigning ads:', err);
          this.$message.error('Error assigning ads');
        });
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.media-container {
  width: 100%;
  /* 原来写死 height:150vh + overflow:hidden：超过 1.5 屏的内容会被直接裁掉，
     广告多的时候最后一行卡片永远只显示一半。改为按内容自适应，
     滚动交给外层的 vue-scroll 处理 */
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--vtm-well);
}

/* Top Header - Filters and Search */
.top-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: var(--vtm-surface);
  border-bottom: 1px solid var(--vtm-line);
  flex-wrap: wrap;
  gap: 20px;
}

.left-section {
  display: flex;
  gap: 12px;
}

.right-section {
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
}

/* Selection Toolbar */
.selection-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background-color: var(--vtm-surface);
  border-bottom: 1px solid var(--vtm-line);
  flex-wrap: wrap;
  gap: 20px;
}

.left-toolbar {
  display: flex;
  align-items: center;
  gap: 30px;
  flex-wrap: wrap;
}

.select-all-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 15px;
  color: var(--vtm-ink);
  font-weight: 500;
  white-space: nowrap;
}

.select-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--vtm-mint);
}

.tag-as-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tag-as-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--vtm-ink);
  white-space: nowrap;
}

.tag-btn {
  padding: 6px 12px;
  color: var(--vtm-ink);
  border: 1.5px solid var(--vtm-line-2);
  background-color: var(--vtm-surface);
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* .tag-btn:hover:not(:disabled) {
  border-color: var(--vtm-mint);
  color: var(--vtm-mint-ink);
} */

/* .tag-btn.active {
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
  border-color: var(--vtm-mint);
} */

/* .tag-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
} */

.right-toolbar {
  display: flex;
  gap: 12px;
}

/* Buttons */
.btn {
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.btn-upload {
  background-color: var(--vtm-mint);
  color: white;
  padding: 10px 18px;
}

.btn-upload:hover {
  background-color: var(--vtm-mint-dim);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

.btn-assign {
  background-color: var(--vtm-warn);
  color: white;
  padding: 10px 18px;
}

.btn-assign:hover {
  background-color: var(--vtm-warn);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.btn-delete {
  background-color: var(--vtm-crit);
  color: white;
  padding: 10px 18px;
}

.btn-delete:hover {
  background-color: var(--vtm-crit);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

/* Filter Buttons */
.filter-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-btn {
  padding: 8px 16px;
  border: none;
  color: var(--vtm-ink);
  background-color: var(--vtm-surface);
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-btn:hover {
  color: var(--vtm-ink-2);
}

.filter-btn.active {
  color: var(--vtm-ink);
  background-color: var(--vtm-well);
  border-bottom: 2px solid var(--vtm-ink);
  font-weight: 600;
}

/* Search Box */
.search-reset {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.search-box {
  padding: 8px 14px;
  border: 1px solid var(--vtm-line);
  border-radius: 6px;
  width: 220px;
  font-size: 14px;
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
}

.search-box::placeholder {
  color: var(--vtm-ink-3);
}

.search-box:focus {
  outline: none;
  border-color: var(--vtm-mint);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.reset-btn {
  padding: 8px 14px;
  border: 1px solid var(--vtm-line);
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  white-space: nowrap;
}



/* Media Grid Wrapper */
.media-grid-wrapper {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  scrollbar-width: none;
}

.media-grid-wrapper::-webkit-scrollbar {
  display: none;
}

/* Media Grid */
.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.media-card {
  background-color: var(--vtm-surface);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  border: 1px solid var(--vtm-line);
}



.card-header {
  position: absolute;
  top: 2px;
  left: 7px;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10;
}

.card-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--vtm-mint);
}

.card-edit-btn {
  padding: 6px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: var(--vtm-ink);
  transition: color 0.2s, background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: large;
}
.card-edit-btn:hover {
  color: var(--vtm-mint-ink);
  background: rgba(255, 255, 255, 1);
}

.card-actions {
  display: flex;
  gap: 8px;
}

.edit-icon {
  width: 32px;
  height: 32px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
}

.edit-icon:hover {
  background-color: var(--vtm-surface);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.card-image {
  width: 100%;
  height: 235px;
  overflow: hidden;
  background-color: var(--vtm-well);
  position: relative;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.card-image video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background-color: #000;
  /* Hide default video controls */
  outline: none;
}

.card-image video::-webkit-media-controls {
  display: none;
}

.card-image video::-webkit-media-controls-panel {
  display: none;
}

.card-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background-color: #000;
  outline: none;
}

/* Play button overlay for videos */
.card-image video::after {
  display: none;
}

/* Add play icon with pseudo-element */
.media-card .card-image::after {
  display: none;
}

/* Only show play button for video items */
.media-card:has(video) .card-image::after {
  display: none;
}

/* Hide play button for non-video items */
.media-card:has(img) .card-image::after {
  display: none;
}

.media-card:hover .card-image img {
  transform: scale(1.1);
  
}
.media-card:hover {
    border: 1px solid var(--vtm-mint);
    box-shadow: 0 4px 12px rgba(59, 124, 244, 0.12);
}

.media-card.selected {
  border: 1px solid var(--vtm-mint);
  box-shadow: 0 4px 12px rgba(59, 124, 244, 0.12);
}

.card-content {
  padding: 14px;
}


.card-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--vtm-ink);
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-desc {
  font-size: 13px;
  color: var(--vtm-ink-2);
  margin-bottom: 8px;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.card-date {
  font-size: 12px;
  color: var(--vtm-ink-3);
}

.card-tag {
  padding: 0px 4px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  border: 1.5px solid;
}

.card-tag i {
  font-size: 13px;
}

.tag-screen {
  background-color: var(--vtm-mint-soft);
  color: var(--vtm-mint-ink);
  border-color: var(--vtm-mint);
}

.tag-banner {
  background-color: var(--vtm-warn-soft);
  color: var(--vtm-warn);
  border-color: var(--vtm-warn);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--vtm-ink-3);
  font-size: 16px;
  grid-column: 1 / -1;
}

/* Responsive */
@media (max-width: 1200px) {
  .media-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  }

  .search-box {
    width: 150px;
  }
}

@media (max-width: 992px) {
  .top-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .right-section {
    width: 100%;
    flex-direction: column;
  }

  .media-grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
}

@media (max-width: 768px) {
  .media-container {
    width: 100%;
  }

  .top-header {
    padding: 12px 16px;
  }

  .selection-toolbar {
    padding: 12px 16px;
  }

  .left-toolbar {
    gap: 16px;
    width: 100%;
  }

  .tag-as-section {
    width: 100%;
    flex-wrap: wrap;
  }

  .right-toolbar {
    width: 100%;
    flex-direction: column;
  }

  .right-toolbar button {
    width: 100%;
  }

  .media-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }

  .media-grid-wrapper {
    padding: 12px;
  }

  .search-box {
    width: 100%;
  }

  .filter-buttons {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .media-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }

  .left-toolbar {
    flex-direction: column;
    width: 100%;
  }

  .select-all-label {
    width: 100%;
  }

  .btn {
    padding: 8px 12px;
    font-size: 12px;
  }

  .tag-btn {
    padding: 3px 8px;
    font-size: 12px;
  }

  .card-title {
    font-size: 13px;
  }

  .card-desc {
    font-size: 12px;
  }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: var(--vtm-surface);
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 550px;
  max-height: 80vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  scrollbar-width: none;
}

.modal-content::-webkit-scrollbar {
  display: none;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--vtm-line);
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: var(--vtm-ink);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  color: var(--vtm-ink-2);
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.modal-close:hover {
  background-color: var(--vtm-well);
  color: var(--vtm-ink);
}

.modal-body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
}

.modal-body::-webkit-scrollbar {
  display: none;
}

.file-upload-area {
  border: 2px dashed var(--vtm-line);
  border-radius: 8px;
  padding: 30px 20px;
  text-align: center;
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: var(--vtm-surface-2);
}

.file-upload-area.dragging {
  border-color: var(--vtm-mint);
  background-color: var(--vtm-mint-soft);
}

.file-upload-area:hover {
  border-color: var(--vtm-mint);
  background-color: var(--vtm-mint-soft);
}

.upload-icon {
  color: var(--vtm-ink);
  font-size: 32px;
  margin-bottom: 10px;
}

.upload-text {
  font-size: 15px;
  font-weight: 500;
  color: var(--vtm-ink);
  margin: 0 0 5px 0;
}

.upload-support {
  font-size: 13px;
  color: var(--vtm-ink-3);
  margin: 0 0 15px 0;
}

.browse-btn {
  padding: 8px 16px;
  background-color: var(--vtm-well);
  border: 1px solid var(--vtm-line);
  border-radius: 4px;
  color: var(--vtm-ink);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.browse-btn:hover {
  background-color: var(--vtm-well);
  border-color: var(--vtm-line-2);
}

.hidden-file-input {
  display: none;
}

.uploaded-file-preview {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  border: 1px solid var(--vtm-line);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 20px;
  background-color: var(--vtm-surface-2);
}

.preview-image-container {
  flex-shrink: 0;
  width: 100px;
  height: 70px;
  border-radius: 4px;
  overflow: hidden;
  background-color: var(--vtm-well);
  position: relative;
}

.preview-image-container img,
.preview-image-container video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: white;
  font-size: 13px;
  border-radius: 4px;
  z-index: 1;
}

.preview-loading-overlay i {
  font-size: 22px;
}

.preview-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-filename {
  font-size: 13px;
  color: var(--vtm-ink);
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  word-break: break-all;
}

.upload-progress-text {
  font-size: 12px;
  color: var(--vtm-mint-ink);
  margin: 0;
}

.upload-success-text {
  font-size: 12px;
  color: var(--vtm-ok);
  margin: 0;
}

.clear-upload-btn {
  align-self: flex-start;
  padding: 5px 10px;
  border: 1px solid var(--vtm-crit);
  border-radius: 4px;
  color: var(--vtm-crit);
  background: var(--vtm-surface);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-upload-btn:hover:not(:disabled) {
  background-color: var(--vtm-crit-soft);
}

.clear-upload-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.placement-options {
  display: flex;
  gap: 10px;
}

.placement-btn {
  flex: 1;
  padding: 9px 12px;
  border: 1.5px solid var(--vtm-line-2);
  border-radius: 6px;
  background-color: var(--vtm-surface);
  color: var(--vtm-ink-2);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.placement-btn.active {
  border-color: var(--vtm-mint);
  color: var(--vtm-mint-ink);
  background-color: var(--vtm-mint-soft);
}

.placement-btn:hover:not(.active) {
  border-color: var(--vtm-line-2);
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--vtm-ink);
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 6px;
  font-size: 14px;
  color: var(--vtm-ink);
  background-color: var(--vtm-surface);
  transition: all 0.3s ease;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: var(--vtm-mint);
  box-shadow: 0 0 0 3px rgba(59, 124, 244, 0.1);
}

.form-input::placeholder {
  color: var(--vtm-ink-3);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid var(--vtm-line);
  background-color: var(--vtm-surface-2);
}

.btn-cancel {
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
  border: 1px solid var(--vtm-line-2);
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.btn-cancel:hover {
  background-color: var(--vtm-well);
  border-color: var(--vtm-line-2);
}

.btn-upload-confirm {
  background-color: var(--vtm-mint);
  color: white;
  border: none;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.btn-upload-confirm:hover {
  background-color: var(--vtm-mint-dim);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

.btn-upload-confirm:disabled {
  background-color: var(--vtm-well);
  cursor: not-allowed;
  box-shadow: none;
}

/* Assign Modal Styles */
.assign-modal {
  max-width: 500px;
}

.assign-info {
  margin-bottom: 20px;
}

.assign-dates-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.assign-dates-row .form-group {
  flex: 1;
  min-width: 0;
}

.assign-dates-row .date-input,
.assign-dates-row .assign-datetime-picker {
  width: 100%;
}

.assign-count {
  font-size: 14px;
  color: var(--vtm-ink-2);
  margin-bottom: 12px;
  display: block;
}

.selected-ads-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.selected-ad-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  border-radius: 4px;
  border: 1px solid var(--vtm-line);
}

.ad-thumbnail {
  width: 35px;
  height: 25px;
  object-fit: cover;
  padding: 2px;
  flex-shrink: 0;
}

.ad-thumbnail-video {
  display: block;
  pointer-events: none;
}

.more-badge {
  border: unset;
  justify-content: center;
}

.more-count {
  margin-right: -8px;
  font-size: 13px;
  color: var(--vtm-ink-2);
}

.ad-title {
  font-size: 12px;
  color: var(--vtm-ink);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 4px;
}

.dropdown-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.dropdown-icon {
  position: absolute;
  left: 12px;
  font-size: 16px;
  pointer-events: none;
  color: var(--vtm-ink);
}

.dropdown-wrapper .form-input {
  padding-left: 40px;
}

.btn-assign-confirm {
  background-color: var(--vtm-mint);
  color: white;
  border: none;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.btn-assign-confirm:hover {
  background-color: var(--vtm-mint-dim);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

.btn-assign-confirm:disabled {
  background-color: var(--vtm-well);
  cursor: not-allowed;
  box-shadow: none;
}

/* Machines Section Styles */
.machines-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.search-machines {
  padding: 10px 12px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 6px;
  font-size: 14px;
}

.machines-list {
  border: 1px solid var(--vtm-line);
  border-radius: 6px;
  max-height: 300px;
  overflow-y: auto;
  background-color: var(--vtm-surface-2);
  scrollbar-width: none;
}

.assign-loading,
.assign-empty {
  padding: 16px;
  text-align: center;
  color: var(--vtm-ink-2);
  font-size: 14px;
}

.assign-loading-item,
.assign-empty-item {
  color: var(--vtm-ink-2);
  font-size: 14px;
  padding: 10px 12px;
}

.machines-list::-webkit-scrollbar {
  display: none;
}

.machine-row {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--vtm-line);
  transition: all 0.2s ease;
  background-color: var(--vtm-surface);
}

.machine-row:last-child {
  border-bottom: none;
}

.machine-row:hover {
  background-color: var(--vtm-surface-2);
}

.machine-row.selected {
  background-color: var(--vtm-mint-soft);
}

.machine-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 12px;
  cursor: pointer;
  accent-color: var(--vtm-mint);
  flex-shrink: 0;
}

.machine-info {
  flex: 1;
  min-width: 0;
}

.machine-id {
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
  margin-bottom: 4px;
}

.machine-location {
  font-size: 12px;
  color: var(--vtm-ink-3);
}

.machine-group {
  font-size: 11px;
  color: var(--vtm-ink-3);
  margin-top: 2px;
}

.machine-settings-btn {
  background: none;
  border: none;
  font-size: 16px;
  color: var(--vtm-ink-3);
  cursor: pointer;
  padding: 4px;
  margin-left: 8px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.machine-settings-btn:hover {
  color: var(--vtm-mint-ink);
}

/* Custom Dropdown Styles */
.custom-dropdown {
  position: relative;
  display: inline-block;
  width: 100%;
}

.dropdown-trigger {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 6px;
  background: var(--vtm-surface);
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--vtm-ink);
  font-family: inherit;
  transition: all 0.3s ease;
}

.dropdown-trigger:hover {
  border-color: var(--vtm-mint);
  box-shadow: 0 0 0 3px rgba(59, 124, 244, 0.1);
}

.dropdown-trigger:focus {
  outline: none;
  border-color: var(--vtm-mint);
  box-shadow: 0 0 0 3px rgba(59, 124, 244, 0.1);
}

.dropdown-trigger i:first-child {
  flex-shrink: 0;
}

.dropdown-arrow {
  margin-left: auto;
  font-size: 12px;
  flex-shrink: 0;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--vtm-surface);
  border: 1px solid var(--vtm-line-2);
  border-top: none;
  border-radius: 0 0 6px 6px;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-top: -1px;
}

.dropdown-item {
  padding: 12px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--vtm-ink);
  background: var(--vtm-surface);
  transition: all 0.2s ease;
  border-bottom: 1px solid var(--vtm-line);
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: var(--vtm-mint-soft);
  color: var(--vtm-mint-ink);
}

.dropdown-item i {
  font-size: 16px;
  flex-shrink: 0;
}

.checkmark {
  margin-left: auto;
  color: var(--vtm-mint-ink);
  font-weight: bold;
}

.checkmark-left {
  color: var(--vtm-ink);
  font-weight: bold;
  margin-right: 8px;
}

.ah-head {
  padding: 16px 16px 0;
}

.ah-seg {
  padding: 10px 16px 8px;
}

.ah-embed {
  overflow-x: auto;
}

/* "How advertising works" 顺序指引 */
.ah-guide {
  margin: 10px 16px 0;
  padding: 12px 14px;
  background: var(--vtm-info-soft);
  border: 1px solid var(--vtm-info);
  border-radius: 8px;
}

.ah-guide__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ah-guide__title {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--vtm-info);
  display: flex;
  align-items: center;
  gap: 6px;
}

.ah-guide__dismiss {
  border: none;
  background: transparent;
  color: var(--vtm-ink-2);
  font-size: 12.5px;
  cursor: pointer;
  padding: 2px 4px;
}

.ah-guide__dismiss:hover {
  color: var(--vtm-ink);
  text-decoration: underline;
}

.ah-guide__steps {
  margin: 8px 0 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.ah-guide__steps li {
  font-size: 12.5px;
  color: var(--vtm-ink);
  line-height: 1.5;
}

.ah-guide-reopen {
  margin: 10px 16px 0;
  border: none;
  background: transparent;
  color: var(--vtm-info);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
}

.ah-guide-reopen:hover {
  text-decoration: underline;
}

/* 轮播与时长（账户级） */
/* This file's own "* { padding: 0 }" reset (above) wins the cascade over
   Element UI's .el-button padding at equal specificity, since scoped styles
   compile to an attribute selector of the same weight and load later. */

</style>